import React from "react"
import { Card, CardContent } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Settings, Share2 } from "lucide-react"
import redirectTo from "../../../utils/redirect"
import { CATEGORIES } from "../../../constants/categories"
import { slugify } from "../../../utils/slugify"
import { useAuth } from "../../../contexts/AuthContext"
import { Event } from "../../../services/EventService"
import { LeaveEventModal } from "../../events/components/LeaveEventModal"

interface EventCardProps {
  id: string
  title: string
  description: string
  imageUrl: string
  category?: string
  parentEventId?: string | null
  type?: 'hosted' | 'attending' | 'user'
  onManage: (id: string) => void
  onShare: (id: string) => void
  onLeft?: () => void
}

export const EventCard = ({ id, title, description, imageUrl, category, parentEventId, type, onManage, onShare, onLeft }: EventCardProps) => {
  const slug = slugify(title)
  const { user } = useAuth()
  const [isLeaving, setIsLeaving] = React.useState(false)
  const [showLeaveModal, setShowLeaveModal] = React.useState(false)

  const handleNavigate = () => {
    const publicUrl = `${window.location.origin}/event/${slug}`
    redirectTo(publicUrl)
  }

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShare(id)
  }

  const handleManage = (e: React.MouseEvent) => {
    e.stopPropagation()
    onManage(id)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleNavigate()
    }
  }

  const handleLeaveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!user) return
    setShowLeaveModal(true)
  }

  const confirmLeave = async () => {
    if (!user) return
    try {
      setIsLeaving(true)
      const { success, error } = await Event.leaveEventAsUser(id, user.id)
      if (!success || error) {
        // Silencioso por ahora; podríamos mostrar toast más adelante
        return
      }
      if (onLeft) onLeft()
      setShowLeaveModal(false)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLeaving(false)
    }
  }

  return (
    <Card
      className="mb-4 cursor-pointer hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
      onClick={handleNavigate}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={`Open event ${title}`}
    >
      <CardContent className="p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3 overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-10 h-10 md:w-16 md:h-16 rounded-lg object-cover flex-shrink-0 border border-gray-100"
            draggable={false}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                style={CATEGORIES.find(cat => cat.name === category)?.color
                  ? {
                      backgroundColor: CATEGORIES.find(cat => cat.name === category)?.color,
                      color: CATEGORIES.find(cat => cat.name === category)?.textColor
                    }
                  : {}}
                className="text-xs px-2 py-1 rounded-md font-medium truncate max-w-[140px] bg-primary/10 text-primary"
                title={category}
              >
                {category}
              </span>
              {parentEventId && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-md font-medium">
                  Eventos Paralelos
                </span>
              )}
            </div>
            <h3 className="font-semibold text-lg leading-tight truncate" title={title}>
              {title}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2">
              {description}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-stretch ml-4 shrink-0">
          {type !== 'user' && (
            <Button
              className="w-full"
              variant="outlinePrimary"
              size="sm"
              onClick={handleManage}
            >
              <Settings className="mr-2 h-4 w-4" />
              Manage
            </Button>
          )}
          <Button
            className={`w-full ${type !== 'user' ? 'mt-1' : ''}`}
            variant="outline"
            size="sm"
            onClick={handleShare}
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
          {type === 'user' && (
            <Button
              className="w-full mt-1"
              variant="outline"
              size="sm"
              onClick={handleLeaveClick}
              disabled={isLeaving}
            >
              {isLeaving ? 'Leaving…' : 'Leave event'}
            </Button>
          )}
        </div>
      </CardContent>
      <LeaveEventModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={confirmLeave}
        eventTitle={title}
        isLoading={isLeaving}
      />
    </Card>
  )
}